export const SITE_URL = "https://cloudtrim.in";

export const serviceSlugs = [
  "website-hosting",
  "web-development",
  "website-design",
  "cloud-consultancy",
  "project-management",
  "crm-management",
];

const serviceDetails = [
  [
    "Website Hosting",
    "Reliable website hosting from Cloudtrim with performance monitoring, SSL, backups and support for your business website.",
  ],
  [
    "Web Development",
    "Cloudtrim builds responsive websites, web applications and integrations designed for performance, accessibility and maintainable growth.",
  ],
  [
    "Website Design",
    "Cloudtrim creates brand-aligned website designs, user experiences, prototypes and design systems for clear, effective digital journeys.",
  ],
  [
    "Cloud Consultancy",
    "Plan and improve cloud architecture, migrations, reliability and costs with Cloudtrim's expertise across AWS, Azure and Google Cloud.",
  ],
  [
    "Project Management",
    "Cloudtrim supports project planning, sprint delivery, stakeholder communication and risk management for your technology projects.",
  ],
  [
    "CRM Management",
    "Improve customer workflows with Cloudtrim's CRM setup, automation, data cleanup, reporting and customer retention support.",
  ],
];

export const routes = [
  {
    path: "/",
    page: "home",
    label: "Home",
    title: "Cloudtrim Technologies LLP | Web, Cloud & Digital Solutions",
    description:
      "Cloudtrim Technologies LLP delivers web development, website design, cloud consultancy, hosting, project management and CRM services.",
  },
  {
    path: "/about/",
    page: "about",
    label: "About Us",
    title: "About Cloudtrim | Technology & Digital Expertise",
    description:
      "Meet Cloudtrim Technologies LLP, a technology team in Maharashtra helping businesses build effective web platforms, cloud systems and digital experiences.",
  },
  {
    path: "/services/",
    page: "services",
    label: "Services",
    title: "Technology Services | Cloudtrim",
    description:
      "Explore Cloudtrim's website hosting, web development, website design, cloud consultancy, project management and CRM management services.",
  },
  {
    path: "/careers/",
    page: "careers",
    label: "Careers",
    title: "Careers at Cloudtrim | Work With Our Team",
    description:
      "Discover the culture and opportunities at Cloudtrim. Connect with our team about contributing to web, cloud and digital technology projects.",
  },
  {
    path: "/blog/",
    page: "blog",
    label: "Blog",
    title: "Cloud & Technology Insights | Cloudtrim Blog",
    description:
      "Read Cloudtrim's perspectives on serverless AI, cloud operations, AIOps and sustainability in modern technology infrastructure.",
  },
  {
    path: "/contact/",
    page: "contact",
    label: "Contact",
    title: "Contact Cloudtrim | Discuss Your Project",
    description:
      "Contact Cloudtrim Technologies LLP to discuss your website, cloud or CRM project. Find our offices in Vita and Wakad, Maharashtra.",
  },
  ...serviceDetails.map(([label, description], serviceIndex) => ({
    path: `/services/${serviceSlugs[serviceIndex]}/`,
    page: "service-detail",
    serviceIndex,
    label,
    title: `${label} Services | Cloudtrim`,
    description,
  })),
  {
    path: "/blog/serverless-ai/",
    page: "blog-detail",
    postId: "serverless-ai",
    label: "Serverless AI",
    title:
      'The "Quiet" Revolution: How Serverless AI is Democratizing Innovation | Cloudtrim',
    description:
      "Explore how serverless inference and cloud-native services can simplify AI delivery and help teams build applications without managing dedicated infrastructure.",
  },
  {
    path: "/blog/aiops/",
    page: "blog-detail",
    postId: "aiops",
    label: "AIOps",
    title:
      'Beyond the Hype: Building "Self-Healing" Clouds with AIOps | Cloudtrim',
    description:
      "Explore AIOps, anomaly detection, predictive maintenance and automated remediation for more reliable cloud operations.",
  },
  {
    path: "/blog/green-cloud/",
    page: "blog-detail",
    postId: "green-cloud",
    label: "The Green Cloud",
    title:
      "The Green Cloud: Can AI Solve Its Own Sustainability Crisis? | Cloudtrim",
    description:
      "Explore how AI-assisted cooling, carbon-aware scheduling and hardware lifecycle planning can support more sustainable cloud infrastructure.",
  },
];

export function getRoute(pathname = "/") {
  const path = pathname === "/" ? "/" : `${pathname.replace(/\/$/, "")}/`;
  return (
    routes.find((route) => route.path === path) || {
      path: "/404.html",
      page: "not-found",
      label: "Page not found",
      title: "Page Not Found | Cloudtrim",
      description:
        "This page could not be found. Explore Cloudtrim's services or contact our team.",
    }
  );
}

export function pagePath(page) {
  return routes.find((route) => route.page === page)?.path || "/";
}

export function schemaForRoute(route) {
  const url = `${SITE_URL}${route.path}`;
  const organizationId = `${SITE_URL}/#organization`;
  const websiteId = `${SITE_URL}/#website`;
  const webpageId = `${url}#webpage`;
  const graph = [
    {
      "@type": "Organization",
      "@id": organizationId,
      name: "Cloudtrim Technologies LLP",
      alternateName: "Cloudtrim",
      url: `${SITE_URL}/`,
      logo: `${SITE_URL}/Images/Logo.png`,
      email: "info@cloudtrim.in",
      telephone: "+91-2067620186",
      address: {
        "@type": "PostalAddress",
        streetAddress: "452/B, Balaji Heights",
        addressLocality: "Vita",
        addressRegion: "Maharashtra",
        postalCode: "415311",
        addressCountry: "IN",
      },
    },
    {
      "@type": "WebSite",
      "@id": websiteId,
      url: `${SITE_URL}/`,
      name: "Cloudtrim",
      publisher: { "@id": organizationId },
      inLanguage: "en",
    },
    {
      "@type":
        route.page === "contact"
          ? "ContactPage"
          : route.page === "about"
            ? "AboutPage"
            : "WebPage",
      "@id": webpageId,
      url,
      name: route.title,
      description: route.description,
      isPartOf: { "@id": websiteId },
      about: { "@id": organizationId },
      inLanguage: "en",
    },
  ];
  if (route.page === "not-found")
    return { "@context": "https://schema.org", "@graph": graph.slice(0, 2) };
  if (route.page === "service-detail") {
    graph[2].mainEntity = { "@id": `${url}#service` };
    graph.push({
      "@type": "Service",
      "@id": `${url}#service`,
      name: route.label,
      serviceType: route.label,
      description: route.description,
      url,
      provider: { "@id": organizationId },
      mainEntityOfPage: { "@id": webpageId },
    });
  }
  if (route.page === "blog-detail") {
    graph[2].mainEntity = { "@id": `${url}#article` };
    graph.push({
      "@type": "BlogPosting",
      "@id": `${url}#article`,
      headline: route.title.replace(/ \| Cloudtrim$/, ""),
      description: route.description,
      url,
      author: { "@id": organizationId },
      publisher: { "@id": organizationId },
      mainEntityOfPage: { "@id": webpageId },
      inLanguage: "en",
    });
  }
  return { "@context": "https://schema.org", "@graph": graph };
}

export function updateMetadata(route) {
  if (typeof document === "undefined") return;
  document.title = route.title;
  const setMeta = (attribute, name, content) => {
    let element = document.head.querySelector(`meta[${attribute}="${name}"]`);
    if (!element) {
      element = document.createElement("meta");
      element.setAttribute(attribute, name);
      document.head.appendChild(element);
    }
    element.setAttribute("content", content);
  };
  const url = `${SITE_URL}${route.path}`;
  setMeta("name", "description", route.description);
  setMeta(
    "name",
    "robots",
    route.page === "not-found" ? "noindex,follow" : "index,follow",
  );
  setMeta("property", "og:title", route.title);
  setMeta("property", "og:description", route.description);
  setMeta("property", "og:url", url);
  setMeta(
    "property",
    "og:type",
    route.page === "blog-detail" ? "article" : "website",
  );
  setMeta("property", "og:image", `${SITE_URL}/Images/Logo.png`);
  setMeta("name", "twitter:title", route.title);
  setMeta("name", "twitter:description", route.description);
  setMeta("name", "twitter:image", `${SITE_URL}/Images/Logo.png`);
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", url);
  let schema = document.getElementById("structured-data");
  if (!schema) {
    schema = document.createElement("script");
    schema.id = "structured-data";
    schema.type = "application/ld+json";
    document.head.appendChild(schema);
  }
  schema.textContent = JSON.stringify(schemaForRoute(route));
}
